import { PartyPopper } from "lucide-react";
import { useEffect, type CSSProperties } from "react";

const COLORS = [
  "#F77119",
  "#FFAB75",
  "#16A34A",
  "#2563EB",
  "#FACC15",
  "#A34108",
];
const PIECES = Array.from({ length: 28 }, (_, i) => ({
  left: (i * 37) % 100,
  delay: (i % 7) * 70,
  x: ((i * 53) % 120) - 60,
  rotate: 360 + ((i * 97) % 360),
  color: COLORS[i % COLORS.length],
  round: i % 3 === 0,
}));

/**
 * Mission-complete moment: CSS confetti (hidden for reduced motion, see index.css) and a short announced message.
 * Closes itself after `durationMs`; it never blocks the page (pointer-events: none).
 */
export const Celebration = ({
  message,
  onDone,
  durationMs = 2600,
}: {
  message: string;
  onDone: () => void;
  durationMs?: number;
}) => {
  useEffect(() => {
    const id = window.setTimeout(onDone, durationMs);
    return () => window.clearTimeout(id);
  }, [onDone, durationMs]);
  return (
    <div
      className="pointer-events-none fixed inset-0 z-50 overflow-hidden"
      data-testid="training-celebration"
    >
      {PIECES.map((p, i) => (
        <span
          key={i}
          aria-hidden="true"
          className="gavion-confetti-piece absolute top-0 block h-3 w-2"
          style={
            {
              left: `${p.left}%`,
              background: p.color,
              borderRadius: p.round ? "9999px" : "2px",
              animationDelay: `${p.delay}ms`,
              "--gavion-confetti-x": `${p.x}vw`,
              "--gavion-confetti-r": `${p.rotate}deg`,
            } as CSSProperties
          }
        />
      ))}
      <div className="absolute inset-x-4 top-1/3 flex justify-center">
        <p
          role="status"
          className="flex items-center gap-2 rounded-2xl bg-primary px-5 py-3 text-lg font-extrabold text-primary-foreground shadow-[0_0_32px_-4px_rgba(247,113,25,.6)] motion-safe:animate-in motion-safe:zoom-in-75 motion-safe:fade-in motion-safe:duration-300"
        >
          <PartyPopper className="size-5 shrink-0" aria-hidden="true" />
          {message}
        </p>
      </div>
    </div>
  );
};
